import env from "$root/env";
import { getCurrentUser } from "$root/functional-areas/user";
import { platformToast, ToastContent } from "$root/notification-system/toast";
import { isAxiosResponse } from "$root/third-party-integrations/axios";
import { dateYYYYMMDD, downloadBlob, tryJsonParse } from "$root/utils";
import { Text } from "@mdotm/shadcn/legacy/components";
import { identity } from "@mdotm/utils/functions";
import axios, { type AxiosResponse } from "axios";
import { REQUEST_TIMEOUT } from "./timeouts";

const knownOffenders = new Set<string>();

export const authAxiosInstance = axios.create({
	timeout: REQUEST_TIMEOUT,
});

/** no auth interceptors, either on request or response */
export const publicAxiosInstance = axios.create({
	timeout: REQUEST_TIMEOUT,
});

if (env.appEnv === "development") {
	authAxiosInstance.interceptors.response.use(identity, (err) => {
		if (getCurrentUser().customerName !== "MDOTM") {
			return Promise.reject(err);
		}
		const response = err != null && typeof err === "object" ? err.response : null;
		if (
			isAxiosResponse(response) &&
			response.status >= 500 &&
			response.status < 600 &&
			!knownOffenders.has(response.config.url ?? "")
		) {
			knownOffenders.add(response.config.url ?? "");
			toastBackendIssue(response);
		}

		return Promise.reject(err);
	});
}

function toastBackendIssue(response: AxiosResponse) {
	platformToast({
		icon: "Alert",
		severity: "error",
		onClick() {
			downloadBlob(
				new Blob([axiosResponseToMarkdown(response)], {
					type: "text/markdown",
				}),
				// `envName`, so the report says WHICH environment produced it: every dynamically created
				// environment shares the `appEnv` behaviour class of a development one.
				{ fileName: `${env.envName}_5xx_${dateYYYYMMDD(new Date())}.md` },
			);
		},
		children: (
			<ToastContent>
				<Text type="Body/M/Book">Backend issue detected. HTTP Status Code: {response.status}</Text>
				<br />
				<Text type="Body/M/Bold">Click this toast to download diagnostic information</Text>
			</ToastContent>
		),
	});
}

function axiosResponseToMarkdown(response: AxiosResponse): string {
	const request = (() => {
		if (response.config.data == null) {
			return null;
		}
		if (typeof response.config.data === "string") {
			return tryJsonParse(response.config.data, null) ?? typeof response.config.data;
		}
		return response.config.data;
	})();
	return [
		`${response.config.method?.toUpperCase() ?? "unknown HTTP Method"}: ${response.config.url}`,
		"",
		`Received HTTP Status: ${[response.status, response.statusText].filter(Boolean).join(" ")}`,
		"",
		"Request payload:",
		...(response == null ? ["<none>"] : ["```json", JSON.stringify(request, undefined, "\t"), "```"]),
		"",
		"Response payload:",
		...(response.data == null ? ["<none>"] : ["```json", JSON.stringify(response.data, undefined, "\t"), "```"]),
		"",
		"Java Stack trace:",
		...(response.data?.debug?.exceptionStackTrace == null
			? ["<none>"]
			: ["```", response.data?.debug?.exceptionStackTrace, "```"]),
	].join("\n");
}
